import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './dash.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from 'react-bootstrap/Spinner';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsRotate, faClockRotateLeft, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { collection, getDocs, getFirestore, limit, orderBy, query, startAfter } from 'firebase/firestore';
import { app } from '../firebase';
import { useNavigate } from 'react-router-dom';

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';

  const dateObj = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return 'Not recorded';

  return dateObj.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getLogDateObject = (value) => {
  if (!value) return null;

  const dateObj = value?.toDate ? value.toDate() : value?.seconds ? new Date(value.seconds * 1000) : new Date(value);
  if (Number.isNaN(dateObj.getTime())) return null;

  return dateObj;
};

const isSameDay = (dateA, dateB) => (
  dateA.getFullYear() === dateB.getFullYear()
  && dateA.getMonth() === dateB.getMonth()
  && dateA.getDate() === dateB.getDate()
);

const LOGS_PAGE_SIZE = 50;

function ActivityLogsComp() {
  const db = getFirestore(app);
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [adminDirectory, setAdminDirectory] = useState({});
  const [adminOptions, setAdminOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastVisibleLog, setLastVisibleLog] = useState(null);
  const [hasMoreLogs, setHasMoreLogs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [selectedDate, setSelectedDate] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);

    try {
      const [logsSnap, adminsSnap] = await Promise.all([
        getDocs(query(collection(db, 'AdminActivity'), orderBy('createdAt', 'desc'), limit(LOGS_PAGE_SIZE))),
        getDocs(collection(db, 'Admins'))
      ]);
      const items = [];
      const directory = {};
      const adminIds = new Set();
      const options = [];

      adminsSnap.forEach((docItem) => {
        const data = docItem.data();
        const adminRecord = {
          userName: data.userName || '',
          userEmail: data.userEmail || '',
          userID: data.userID || docItem.id
        };
        const primaryId = String(docItem.id || '').trim();
        const secondaryId = String(data.userID || '').trim();

        if (primaryId) {
          directory[primaryId] = adminRecord;
          adminIds.add(primaryId);
        }

        if (secondaryId) {
          directory[secondaryId] = adminRecord;
          adminIds.add(secondaryId);
        }

        options.push({
          value: secondaryId || primaryId,
          label: data.userName || data.userEmail || secondaryId || primaryId
        });
      });

      logsSnap.forEach((docItem) => {
        const logData = { id: docItem.id, ...docItem.data() };
        const logAdminId = String(logData.adminId || '').trim();

        if (logAdminId && adminIds.has(logAdminId)) {
          items.push(logData);
        }
      });

      setLogs(items);
      setAdminDirectory(directory);
      setAdminOptions(options.sort((first, second) => first.label.localeCompare(second.label)));
      setLastVisibleLog(logsSnap.docs[logsSnap.docs.length - 1] || null);
      setHasMoreLogs(logsSnap.docs.length === LOGS_PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load activity logs:', error);
      setLogs([]);
      setAdminDirectory({});
      setAdminOptions([]);
      setLastVisibleLog(null);
      setHasMoreLogs(false);
    } finally {
      setLoading(false);
    }
  }, [db]);

  const loadMoreLogs = useCallback(async () => {
    if (!lastVisibleLog || loadingMore || !hasMoreLogs) return;

    setLoadingMore(true);
    try {
      const logsSnap = await getDocs(query(
        collection(db, 'AdminActivity'),
        orderBy('createdAt', 'desc'),
        startAfter(lastVisibleLog),
        limit(LOGS_PAGE_SIZE)
      ));
      const items = [];
      logsSnap.forEach((docItem) => {
        const logData = { id: docItem.id, ...docItem.data() };
        const logAdminId = String(logData.adminId || '').trim();
        if (logAdminId && adminDirectory[logAdminId]) items.push(logData);
      });

      setLogs((current) => [...current, ...items]);
      setLastVisibleLog(logsSnap.docs[logsSnap.docs.length - 1] || lastVisibleLog);
      setHasMoreLogs(logsSnap.docs.length === LOGS_PAGE_SIZE);
    } catch (error) {
      console.error('Failed to load more activity logs:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [adminDirectory, db, hasMoreLogs, lastVisibleLog, loadingMore]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const filteredLogs = useMemo(() => {
    const queryText = searchTerm.trim().toLowerCase();

    return logs.filter((log) => {
      const adminId = String(log.adminId || '').trim();
      const adminRecord = adminDirectory[adminId];
      const logDate = getLogDateObject(log.createdAt);
      const selectedDateObject = selectedDate ? new Date(`${selectedDate}T00:00:00`) : null;

      if (selectedAdmin && adminId !== selectedAdmin) {
        return false;
      }

      if (selectedDateObject && (!logDate || !isSameDay(logDate, selectedDateObject))) {
        return false;
      }

      if (!queryText) {
        return true;
      }

      const haystack = [
        log.adminName,
        adminRecord?.userName,
        adminRecord?.userEmail,
        log.action,
        log.details,
        log.path,
        log.page,
        log.setting
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(queryText);
    });
  }, [adminDirectory, logs, searchTerm, selectedAdmin, selectedDate]);

  const stats = useMemo(() => {
    const uniqueAdmins = new Set();
    let openedPageCount = 0;
    let todayCount = 0;
    const today = new Date();

    filteredLogs.forEach((log) => {
      if (log.adminId) {
        uniqueAdmins.add(String(log.adminId));
      }

      if (log.action === 'Opened Page') {
        openedPageCount += 1;
      }

      const value = getLogDateObject(log.createdAt);
      if (value && !Number.isNaN(value.getTime()) && isSameDay(value, today)) {
        todayCount += 1;
      }
    });

    return {
      totalLogs: filteredLogs.length,
      adminCount: uniqueAdmins.size,
      openedPageCount,
      todayCount
    };
  }, [filteredLogs]);

  const getPhoneLogTarget = (log) => {
    if (log?.studentId) {
      return { studentId: log.studentId, studentName: log.studentName || 'student' };
    }

    const details = log?.details || '';
    const match = details.match(/^Viewed phone for (.+) \((.+)\)$/);
    if (!match) return null;

    return { studentName: match[1] || 'student', studentId: match[2] || '' };
  };

  const getAdminLabels = (log) => {
    const adminRecord = adminDirectory[String(log.adminId || '')];
    const primaryLabel = adminRecord?.userEmail || log.adminName || 'Admin';
    const secondaryLabel = adminRecord?.userName || '';

    return { primaryLabel, secondaryLabel };
  };

  return (
    <div className="clientsComp applicationsPage activityLogsPage">
      <div className="appsHeader">
        <div>
          <p className="appsKicker">Audit Trail</p>
          <h4>User Activity Logs</h4>
          <p className="appsSubtext">Review how admins are using the platform across admissions, finance, communication, and settings.</p>
        </div>
        <div className="appsHeaderStats">
          <div className="appsStatChip">
            <span>Loaded logs</span>
            <strong>{stats.totalLogs}</strong>
          </div>
          <div className="appsStatChip">
            <span>Admins loaded</span>
            <strong>{stats.adminCount}</strong>
          </div>
          <div className="appsStatChip">
            <span>Page opens loaded</span>
            <strong>{stats.openedPageCount}</strong>
          </div>
          <div className="appsStatChip">
            <span>Today loaded</span>
            <strong>{stats.todayCount}</strong>
          </div>
        </div>
      </div>

      <div className="appsFiltersCard activityLogsToolbar">
        <InputGroup className="appsSearch">
          <InputGroup.Text>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search loaded logs by admin, action, details, or path"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </InputGroup>

        <div className="appsFilterGroup">
          <Form.Select
            className="filterSelect"
            value={selectedAdmin}
            onChange={(event) => setSelectedAdmin(event.target.value)}
          >
            <option value="">All admins</option>
            {adminOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="date"
            className="filterSelect activityLogsDateFilter"
            value={selectedDate}
            onChange={(event) => setSelectedDate(event.target.value)}
          />
        </div>

        <div className="incomeToolbarMeta activityLogsMeta">
          <span className="incomeToolbarText">{filteredLogs.length} of {logs.length} loaded logs</span>
          <span className="incomeInlineDot"></span>
          <span className="incomeToolbarText">Sorted by newest first</span>
        </div>

        <Button variant="outline-dark" onClick={fetchLogs} disabled={loading || loadingMore}>
          <FontAwesomeIcon icon={faArrowsRotate} />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="incomeStateCard">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading logs...</span>
          </Spinner>
          <p>Loading user activity logs...</p>
        </div>
      ) : (
        <div className="appsTableCard">
          <div className="appsTableHeader">
            <h6>Recent activity</h6>
            <span><FontAwesomeIcon icon={faClockRotateLeft} /> Newest records first</span>
          </div>
          <div className="appsTableWrap activityLogsTableWrap">
            <div className="table-responsive">
              <table className="table appsTable activityLogsTable">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>Path</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan="5" className="appsEmptyState">
                        No activity logs match your search yet.
                      </td>
                    </tr>
                  )}

                  {filteredLogs.map((log) => {
                    const phoneTarget = log.action === 'Viewed student phone number'
                      ? getPhoneLogTarget(log)
                      : null;
                    const { primaryLabel, secondaryLabel } = getAdminLabels(log);

                    return (
                      <tr key={log.id}>
                        <td>{formatDateTime(log.createdAt)}</td>
                        <td>
                          <div className="activityLogActor">
                            <strong>{primaryLabel}</strong>
                            {secondaryLabel && <span>{secondaryLabel}</span>}
                          </div>
                        </td>
                        <td>
                          <span className="activityLogAction">{log.action || 'Activity'}</span>
                        </td>
                        <td>
                          {phoneTarget?.studentId ? (
                            <div className="activityLogDetail">
                              <span>Viewed phone for {phoneTarget.studentName}</span>
                              <button
                                type="button"
                                className="appsViewBtn"
                                onClick={() => navigate('/view-student', { state: { myData: phoneTarget.studentId } })}
                              >
                                View Profile
                              </button>
                            </div>
                          ) : (
                            log.details || 'No extra details'
                          )}
                        </td>
                        <td>
                          <span className="activityLogPath">{log.path || 'N/A'}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {hasMoreLogs && (
            <div className="activityLogsPagination">
              <Button variant="outline-dark" onClick={loadMoreLogs} disabled={loadingMore}>
                {loadingMore ? 'Loading older logs…' : 'Load older logs'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ActivityLogsComp;
